import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContentText,
  FormHelperText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { connect } from 'react-redux'
import cn from 'classnames'
import ButtonSubmit from '../../common/ButtonSubmit'
import {
  getSelectionDetails,
  isRowSelected,
  parseFullName,
  ROWS_PER_PAGE,
} from '../admin/adminUtils'
import { themeConWeb } from '../../../util/modernThemeConweb'
import AltHeaderTableMembers from '../common/AltHeaderTableMembers'
import DialogContent from './DialogContent'
import { PROGRAM_DIALOG_HEIGHT } from './programUtils'

const useStyles = makeStyles({
  circularProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
  },
  row: {
    backgroundColor: themeConWeb.color.greyShade1,
    color: themeConWeb.color.black,
  },
  tableCell: {
    padding: '0px 16px',
  },
  checkboxCell: {
    width: '20px',
  },
  altHeaderRow: {
    backgroundColor: themeConWeb.color.greyShade2,
    color: themeConWeb.color.greyShade6,
  },
  error: {
    color: themeConWeb.color.twoShade3,
  },
})

const StepProgramMembers = ({ teamMembers, loading, onBack, values, setFieldValue }) => {
  const classes = useStyles()
  const memberCount = teamMembers.length
  const [page, setPage] = useState(0)
  const [rows, setRows] = useState(
    memberCount > ROWS_PER_PAGE ? teamMembers.slice(0, ROWS_PER_PAGE) : teamMembers,
  )
  const [selectedMembers, setSelectedMembers] = useState(teamMembers)

  const columns = [
    {
      id: 'name',
      label: 'Name',
    },
    { id: 'email', label: 'Email' },
  ]

  useEffect(() => {
    setFieldValue(
      'members',
      selectedMembers.map(member => member.userId),
    )
  }, [setFieldValue, selectedMembers])

  const { allSelected, allPageSelected, someSelectedOnPage } = getSelectionDetails(
    teamMembers,
    rows,
    selectedMembers,
  )

  const toggleSelectedMember = member => {
    if (isRowSelected(selectedMembers, member)) {
      setSelectedMembers(selectedMembers.filter(sm => sm !== member))
    } else {
      setSelectedMembers(selectedMembers.concat([member]))
    }
  }

  const toggleSelectedPage = () => {
    rows.every(row => isRowSelected(selectedMembers, row))
      ? setSelectedMembers(selectedMembers.filter(sm => !rows.includes(sm)))
      : setSelectedMembers([...new Set(selectedMembers.concat(rows))])
  }

  const toggleAll = () => {
    teamMembers.every(member => isRowSelected(selectedMembers, member))
      ? setSelectedMembers([])
      : setSelectedMembers(teamMembers)
  }

  const handleChangePage = (e, nextPage) => {
    if (memberCount > (nextPage + 1) * ROWS_PER_PAGE) {
      setRows(teamMembers.slice(nextPage * ROWS_PER_PAGE, (nextPage + 1) * ROWS_PER_PAGE))
    } else {
      setRows(teamMembers.slice(nextPage * ROWS_PER_PAGE))
    }
    setPage(nextPage)
  }

  return (
    <>
      <DialogContent height={PROGRAM_DIALOG_HEIGHT}>
        <DialogContentText>Team Members Selected ({values.members.length})</DialogContentText>
        {teamMembers && (
          <>
            <TableContainer component={Paper}>
              {loading && <CircularProgress className={classes.circularProgress} />}
              <Table>
                <TableHead>
                  <TableRow className={classes.row}>
                    <TableCell className={cn([classes.tableCell, classes.checkboxCell])}>
                      <Checkbox
                        indeterminate={someSelectedOnPage && !allPageSelected}
                        checked={allPageSelected}
                        onChange={toggleSelectedPage}
                        inputProps={{ 'aria-label': 'select all members' }}
                      />
                    </TableCell>
                    {columns.map(cell => {
                      return (
                        <TableCell key={cell.id} className={classes.tableCell}>
                          {cell.label}
                        </TableCell>
                      )
                    })}
                  </TableRow>

                  {allPageSelected && (
                    <TableRow className={classes.altHeaderRow}>
                      <TableCell colSpan={columns.length + 1} className={classes.tableCell}>
                        <AltHeaderTableMembers
                          allSelected={allSelected}
                          allPageSelected={allPageSelected}
                          totalRows={memberCount}
                          totalRowsOnPage={rows.length}
                          numSelected={selectedMembers.length}
                          onSelectAll={toggleAll}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {rows.map((member, index) => {
                    const labelId = `members-table-checkbox-${index}`
                    return (
                      <TableRow key={member.userId}>
                        <TableCell>
                          <Checkbox
                            checked={selectedMembers.includes(member)}
                            inputProps={{ 'aria-labelledby': labelId }}
                            onClick={() => toggleSelectedMember(member)}
                          />
                        </TableCell>
                        <TableCell id={labelId}>
                          <Box>
                            <Typography variant="body1">{parseFullName(member)}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell id={labelId}>
                          <Box>
                            <Typography variant="body1">{member.email}</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {memberCount > ROWS_PER_PAGE && (
              <TablePagination
                component="div"
                count={memberCount}
                page={page}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[]}
                onChangePage={handleChangePage}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {selectedMembers.length === 0 && (
          <Box paddingY={2}>
            <FormHelperText className={classes.error}>
              Are you sure you want to create a subsidy without any members?
            </FormHelperText>
          </Box>
        )}
        <Button onClick={onBack}>Back</Button>
        <ButtonSubmit color="primary" variant="contained">
          Next
        </ButtonSubmit>
      </DialogActions>
    </>
  )
}

const mapStateToProps = state => {
  const { search, sort } = state.teamMembers

  return { search, sort }
}

const mapDispatchToProps = {}

export default connect(mapStateToProps, mapDispatchToProps)(StepProgramMembers)
